import { Button } from "@/components/core/buttons/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import Select from "@/components/core/forms/select";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { useProjectCategoryAttachments } from "@/hooks/use-enums";
import { useCreateProjectAttachmentMutation } from "@/hooks/use-project-attachments";
import type { CreateProjectAttachmentRequest } from "@/types/project-attachment";
import { IconUpload } from "@tabler/icons-react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Textarea } from "../core/forms/textarea";

interface UploadAttachmentModalContentProps {
	projectId: string;
	onSuccess?: () => void;
}

interface FormData {
	type: string;
	file: File | null;
	description: string;
	document_date: string;
}

export function UploadAttachmentModalContent({ projectId, onSuccess }: UploadAttachmentModalContentProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const createMutation = useCreateProjectAttachmentMutation();
	const { data: attachmentTypes, isLoading: isLoadingTypes } = useProjectCategoryAttachments();
	const { closeModal } = useModalControl();

	const form = useForm<FormData>({
		defaultValues: {
			type: "",
			file: null,
			description: "",
			document_date: "",
		},
	});

	const { handleSubmit, control, reset, setValue } = form;

	const onSubmit = async (data: FormData) => {
		if (!data.file) {
			toast.error("Please select a file to upload");
			return;
		}

		setIsSubmitting(true);
		try {
			const requestData: CreateProjectAttachmentRequest = {
				project_id: projectId,
				type: data.type,
				file: data.file,
				description: data.description || undefined,
				document_date: data.document_date || undefined,
			};

			await createMutation.mutateAsync(requestData);
			toast.success("Attachment uploaded successfully");
			onSuccess?.();
			closeModal();
			reset();
		} catch (error) {
			console.error("Upload failed:", error);
			toast.error("Failed to upload attachment");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<FormProvider {...form}>
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
				{/* Attachment Type */}
				<FormField
					control={control}
					name="type"
					rules={{ required: "Please select an attachment type" }}
					render={({ field }) => (
						<FormItem>
							<FormLabel required>Attachment Type</FormLabel>
							<div style={{ position: "relative", zIndex: 999 }}>
								<Select
									placeholder="Select attachment type"
									options={
										attachmentTypes?.map((type) => ({
											value: type.value,
											label: `${type.label} - ${type.description}`,
										})) || []
									}
									defaultValue={field.value}
									onValueChange={field.onChange}
									disabled={isLoadingTypes || isSubmitting}
									portal={false}
								/>
							</div>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* File Upload */}
				<FormField
					control={control}
					name="file"
					rules={{ required: "Please select a file to upload" }}
					render={({ field }) => (
						<FormItem>
							<FormLabel required>File</FormLabel>
							<FormControl>
								<div style={{ position: "relative", zIndex: 1 }}>
									<InputFile
										accept="*/*"
										setFile={(file) => {
											field.onChange(file);
											setValue("file", file);
										}}
										disabled={isSubmitting}
									/>
								</div>
							</FormControl>
							<p className="text-xs text-muted-foreground">Maximum file size: 20MB. All file types are supported.</p>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* Document Date */}
				<FormField
					control={control}
					name="document_date"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Document Date (Optional)</FormLabel>
							<FormControl>
								<Input type="date" {...field} disabled={isSubmitting} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* Description */}
				<FormField
					control={control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Description (Optional)</FormLabel>
							<FormControl>
								<Textarea className="input" {...field} placeholder="description" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* Form Actions */}
				<div className="flex justify-end gap-2 pt-4">
					<Button type="button" variant="ghost" onClick={closeModal} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button type="submit" disabled={isSubmitting}>
						{isSubmitting ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
								Uploading...
							</>
						) : (
							<>
								<IconUpload className="w-4 h-4 mr-2" />
								Upload Attachment
							</>
						)}
					</Button>
				</div>
			</form>
		</FormProvider>
	);
}
